import React, { useEffect, useState } from 'react'
import { Products } from 'stylewhere/api'
import { AppStore } from 'stylewhere/shared'
import { Image } from './Image'
import { Spinner } from '@chakra-ui/react'
import { Box } from 'stylewhere/components'
import styled from '@emotion/styled'

interface Props {
  product?: any
  type?: string
  width?: number | string
  height?: number | string
  style?: React.CSSProperties
  variant?: 'boxed'
  transparent?: boolean
  maxHeightContainer?: number
  boxPadding?: string
  alt?: string
  'data-testid'?: string
  onEmptyImageComponent?: React.ReactNode
}

type ImageState = { status: 'loading' } | { status: 'ready'; src: string } | { status: 'error' }

export const PrivateImage = ({ product, type = 'medium', ...imageProps }: Props) => {
  const [state, setState] = useState<ImageState>({ status: 'loading' })

  useEffect(() => {
    if (!product || !AppStore.authToken) {
      setState({ status: 'error' })
      return
    }

    let cancelled = false

    const settings = AppStore.getShowProductImageSettings()
    const isPrivate = settings === 'StylewherePrivateUrl' || settings.toLowerCase() === 'yes'
    let publicUrl
    if (isPrivate) {
      const imageId = Products.getImageID(product)
      if (imageId && imageId !== '' && !Products.isProductImage(imageId)) {
        // L'object URL è gestito dalla cache LRU (Products.fetchPrivateImage):
        // NON va revocato qui, altrimenti si romperebbero le altre istanze che
        // riusano lo stesso blob dalla cache.
        Products.fetchPrivateImage(imageId, type).then((url) => {
          if (!cancelled) {
            setState(url ? { status: 'ready', src: url } : { status: 'error' })
          }
        })
      } else {
        publicUrl = Products.parseImageUrl(imageId, type)
        setState(publicUrl ? { status: 'ready', src: publicUrl } : { status: 'error' })
      }
    } else {
      publicUrl = Products.getPublicImageUrl(product, type)
      setState(publicUrl ? { status: 'ready', src: publicUrl } : { status: 'error' })
    }

    return () => {
      cancelled = true
    }
  }, [product, type])

  if (state.status === 'loading') {
    const loaderWidth = imageProps.width ?? 100
    return (
      <Loader
        center
        style={{
          height: imageProps.height ?? 100,
          minHeight: imageProps.height ?? 100,
          maxHeight: imageProps.height ?? 100,
          maxWidth: loaderWidth,
          width: loaderWidth,
          minWidth: loaderWidth,
          ...imageProps.style
        }}
      >
        <Spinner color="#e0e0e0" size="xl" />
      </Loader>
    )
  }

  if (state.status === 'error') {
    return <Image src={undefined} {...imageProps} />
  }

  // key forces Image to remount with the correct initial src
  // (Image captures src in useState on mount, so remount is needed when src changes)
  return <Image key={state.src} src={state.src} {...imageProps} />
}

const Loader = styled(Box)<{ transparent?: boolean }>`
  font-weight: 800;
  font-size: 10pt;
  text-align: center;
  color: rgba(0, 0, 0, 0.1);
  background-color: transparent;
  border-radius: 10px;
  margin: auto;
`
